// External modules
import styled from '@emotion/styled';
import type { SerializedStyles } from '@emotion/core';
import { css } from '@emotion/core';
import { Text } from '@paypalcorp/pp-react-text-weasley-v6';
import { Link } from '@paypalcorp/pp-react-links-weasley-v6';
import { Button } from '@paypalcorp/pp-react-buttons-weasley-v6';
import { CheckCircleAltIcon } from '@paypalcorp/pp-react-icons-weasley-v6';

// Internal modules
import {
  BUTTON_FOCUSED_COLOR,
  GREYS_BLACK_COLOR,
  HIGH_CONTRAST_TEXT_COLOR,
  INCENTIVES_BLUE_COLOR,
  INCENTIVES_GREEN_COLOR_V1,
  INCENTIVES_GREEN_COLOR_V2,
  INCENTIVES_LILAC_COLOR,
  INCENTIVES_YELLOW_COLOR,
  INFO_MESSAGE_BACKGROUND_COLOR_V2,
  INFO_MESSAGE_BACKGROUND_COLOR_V3,
  PAYPAL_OPEN_FONT,
  PP_BORDER_LOW_CONTRAST_COLOR,
  WHITE_COLOR,
} from '@/styles/styleVariables';
import { getLinkStateStyles, primaryTextButtonStyles } from '../Common';

const incentiveCardStyles = (color: string): SerializedStyles => css`
  will-change: filter;
  filter: drop-shadow(0 2px 16px ${PP_BORDER_LOW_CONTRAST_COLOR});
  padding: 1rem;
  border-radius: 6px;
  margin-top: -2px;
  background-color: ${color};
  position: relative;

  &::after {
    content: ' ';
    position: absolute;
    bottom: 100%;
    left: 91%;
    margin-left: -5px;
    border-width: 9px;
    border-style: solid;
    border-color: transparent transparent ${color};
  }
`;

const offerIconStyles = (color: string): SerializedStyles => css`
  align-items: center;
  background-color: ${color};
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  padding: 2px 0 2px 4px;
  position: relative;
`;

const boldTextStyles = (color: string): SerializedStyles => css`
  color: ${color};
  font-family: ${PAYPAL_OPEN_FONT};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 14px;
  text-align: left;
  white-space: nowrap;
`;

const offerIconArrowStyles = (color: string): SerializedStyles => css`
  content: ' ';
  border-width: 8px;
  border-style: solid;
  border-color: transparent ${color} transparent transparent;
  height: 100%;
  position: absolute;
  right: 0;
`;

const offerDescriptionStyles = (color: string): SerializedStyles => css`
  color: ${color};
  font-family: ${PAYPAL_OPEN_FONT};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 20px;
  text-align: left;
`;

const termsLinkTextStyles = (color: string): SerializedStyles => css`
  font-family: ${PAYPAL_OPEN_FONT};
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1.35rem;
  color: ${color};
  text-decoration: underline;

  &:visited {
    color: ${color};
  }
`;

export const IncentiveCardV1 = styled.div`
  ${incentiveCardStyles(INCENTIVES_LILAC_COLOR)};
`;

export const IncentiveCardV2 = styled.div`
  ${incentiveCardStyles(INCENTIVES_YELLOW_COLOR)};
`;

export const OfferIconV1 = styled.div`
  ${offerIconStyles(INCENTIVES_YELLOW_COLOR)};
`;

export const OfferIconV2 = styled.div`
  ${offerIconStyles(INCENTIVES_BLUE_COLOR)};
`;

export const OfferIconV3 = styled.div`
  ${offerIconStyles(INCENTIVES_BLUE_COLOR)};
  margin-bottom: 7px;
`;

export const BoldTextV1 = styled(Text)`
  ${boldTextStyles(HIGH_CONTRAST_TEXT_COLOR)};
  padding-right: 13px;
`;

export const BoldTextV2 = styled(Text)`
  ${boldTextStyles(WHITE_COLOR)};
  padding-right: 15px;
`;

export const BoldTextV3 = styled(Text)`
  ${boldTextStyles(WHITE_COLOR)};
  font-weight: 700;
  line-height: 20px;
  padding-right: 25px;
`;

export const OfferIconArrowV1 = styled.div`
  ${offerIconArrowStyles(INCENTIVES_LILAC_COLOR)};
`;

export const OfferIconArrowV2 = styled.div`
  ${offerIconArrowStyles(INCENTIVES_YELLOW_COLOR)};
`;

export const OfferIconArrowV3 = styled.div`
  ${offerIconArrowStyles(INCENTIVES_YELLOW_COLOR)};
  border-width: 14px;
`;

export const OfferV1Description = styled(Text)`
  ${offerDescriptionStyles(WHITE_COLOR)};
  padding-left: 1rem;
`;

export const OfferV2Description = styled(Text)`
  ${offerDescriptionStyles(HIGH_CONTRAST_TEXT_COLOR)};
  padding-left: 1rem;
`;

export const OfferV3Description = styled(Text)`
  ${offerDescriptionStyles(HIGH_CONTRAST_TEXT_COLOR)};
`;

export const TermsLinkTextV1 = styled(Link)`
  ${termsLinkTextStyles(WHITE_COLOR)};
`;

export const TermsLinkTextV2 = styled(Link)`
  ${termsLinkTextStyles(HIGH_CONTRAST_TEXT_COLOR)};

  &:active {
    text-decoration: none;
  }
`;

export const IncentiveRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  padding: 0;
`;

export const IncentiveCol = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
`;

export const OfferV2Container = styled(IncentiveRow)`
  background: ${INFO_MESSAGE_BACKGROUND_COLOR_V2};
  padding: 8px 16px 10px;
  align-items: start;
`;

export const GetOfferButton = styled(Button)`
  border-color: ${BUTTON_FOCUSED_COLOR};
  color: ${BUTTON_FOCUSED_COLOR};
  border: 2px solid ${BUTTON_FOCUSED_COLOR};
  font-family: ${PAYPAL_OPEN_FONT};
  font-weight: 700;
  height: fit-content;
  min-width: 101px;
  z-index: 9;
  width: fit-content;
`;

export const ExpiryDate = styled.span`
  color: ${GREYS_BLACK_COLOR};
  font-weight: bold;
  font-family: ${PAYPAL_OPEN_FONT};
`;

export const SuccessText = styled(Text)`
  color: ${GREYS_BLACK_COLOR};
  text-align: center;
  padding-top: 1rem;
  font-family: ${PAYPAL_OPEN_FONT};
`;

export const OfferV2Background = styled.div`
  background: ${INFO_MESSAGE_BACKGROUND_COLOR_V2};
`;

export const OfferAcceptedV3Background = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  background-color: ${INFO_MESSAGE_BACKGROUND_COLOR_V3};
  border: 1px solid ${INCENTIVES_GREEN_COLOR_V1};
  border-radius: 6px;
  padding: 21px 24px 16px;
`;

export const OfferAcceptedV3CheckCircleAltIcon = styled(CheckCircleAltIcon)`
  color: ${INCENTIVES_GREEN_COLOR_V2};
  height: 30px;
  margin-bottom: 13px;
  width: 30px;
`;

export const OfferAcceptedV3Header = styled(Text)`
  color: ${HIGH_CONTRAST_TEXT_COLOR};
  font-family: ${PAYPAL_OPEN_FONT};
  font-size: 16px;
  line-height: 21px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0;
`;

export const OfferAcceptedV3Text1 = styled(SuccessText)`
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  padding: 12px 0 0;
`;

export const SuccessTermsLinkText = styled.a`
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1.35rem;
  text-decoration: none;
  ${({ theme }) => primaryTextButtonStyles(theme)};
  ${({ theme }) => getLinkStateStyles(theme)};
`;

export const SuccessTermsLinkTextV3 = styled(Link)`
  font-family: ${PAYPAL_OPEN_FONT};
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1.35rem;
  text-decoration: none;
  align-self: flex-start;
`;

export const OfferV2Row = styled.div`
  display: flex;
`;

export const OfferV2ButtonContainer = styled.div`
  min-width: 42%;
`;

export const OfferV2DescriptionContainer = styled.div`
  padding-right: 16px;
`;

export const ExpandedValuePropsContainer = styled.div`
  border: 1px solid ${PP_BORDER_LOW_CONTRAST_COLOR};
  border-radius: 12px;
  padding: 16px 12px;
`;

export const ExpandedValuePropsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`;

export const ExpandedValuePropsRowV2 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between; /* Distribute items evenly */
`;

export const ExpandedValuePropsRowV3 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ExpandedValuePropsRowThreeIcons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

export const ExpandedValuePropsText = styled(Text)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

export const PurchaseProtectionV2ContentContainer = styled.div`
  min-width: 199px;
`;

export const PurchaseProtectionV2IconContainer = styled.div`
  min-width: 60px;
`;

export const StretchRewardsContentContainer = styled.div`
  min-width: 247px;
`;

export const StretchRewardsHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const AccountBenefitsContentContainer = styled.div`
  display: flex;
  padding-top: 12px;
  width: '100%';
`;

export const StretchRewardsText = styled(Text)`
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

export const RewardsText = styled(Text)`
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

export const StretchRewardsOfferDisclaimer = styled(Text)`
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.3px;
  line-height: 12px;
  margin-top: 8px;
`;
